import z from 'zod';
import { AllCharLabelSchema, UUIDSchema, UnixTimestampSchema } from '../common/schemas';
import { DBTimestampSchema } from '../common/sql';
import { TaxonomyStatus } from './enums';

// String bounds mirror the tables' own CHECK constraints; a tighter one would
// warn on rows the database accepts.

/** A key describes the thing, never where it sits, so refiling never disturbs one. */
export const DBTaxonomyKeySchema = z
  .string()
  .regex(/^[a-z0-9]+([-/][a-z0-9]+)*$/, 'A key is lowercase words joined by - or /')
  .nullable();

export const DBTopicNameSchema = AllCharLabelSchema.min(1);
export const DBTopicDescriptionSchema = AllCharLabelSchema.nullable();
export const DBTopicStatusSchema = z.enum(TaxonomyStatus);

export const DBTopicSchema = z.object({
  id: UUIDSchema,
  key: DBTaxonomyKeySchema,
  name: DBTopicNameSchema,
  description: DBTopicDescriptionSchema,
  kindId: UUIDSchema.nullable(),
  status: DBTopicStatusSchema,
  mergedIntoTopicId: UUIDSchema.nullable(),
  createdBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBTopicAliasSchema = z.object({
  id: UUIDSchema,
  topicId: UUIDSchema,
  alias: AllCharLabelSchema.min(1),
  createdAt: DBTimestampSchema,
});

/**
 * A topic can be catalogued by two subjects, so anything subject-specific lives
 * here rather than on the topic.
 */
export const DBTopicSubjectSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  sourceTopicId: z.string().min(1).nullable(),
  mappingScopeId: UUIDSchema,
  primaryGeneralGroupId: UUIDSchema.nullable(),
  taggingNote: AllCharLabelSchema.nullable(),
  reviewSources: z.array(z.string()).nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBTopicSubjectSyllabusSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  groupingId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

/** Where a tagger should look instead when a broader topic has a narrower one. */
export const DBTopicRelatedSpecificSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  specificTopicId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

/** Where a topic retired before this catalogue existed went. */
export const DBTopicMigrationSchema = z.object({
  id: UUIDSchema,
  subjectId: UUIDSchema,
  legacyTopicId: z.string().min(1),
  kindId: UUIDSchema,
  reason: AllCharLabelSchema.nullable(),
  createdAt: DBTimestampSchema,
});

export const DBTopicMigrationTargetSchema = z.object({
  migrationId: UUIDSchema,
  topicId: UUIDSchema,
  position: z.number().int(),
  createdAt: DBTimestampSchema,
});

/**
 * Client Schemas
 */

export const TopicKeySchema = DBTaxonomyKeySchema.openapi({
  description: 'Readable handle for a topic, derived from its own name',
  example: 'resolving-launch-velocity',
});

export const TopicNameSchema = DBTopicNameSchema.openapi({
  description: 'What the topic is called',
  example: 'Resolving launch velocity',
});

export const TopicStatusSchema = z.enum(TaxonomyStatus).openapi({
  description: 'Whether the topic is still assigned, or retired',
  example: TaxonomyStatus.Active,
});

export const TopicSchema = z.object({
  id: UUIDSchema,
  key: TopicKeySchema,
  name: TopicNameSchema,
  description: DBTopicDescriptionSchema,
  kindId: UUIDSchema.nullable(),
  status: TopicStatusSchema,
  mergedIntoTopicId: UUIDSchema.nullable(),
  createdBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const TopicAliasSchema = z.object({
  id: UUIDSchema,
  topicId: UUIDSchema,
  alias: AllCharLabelSchema.min(1),
  createdAt: UnixTimestampSchema,
});

export const TopicSubjectSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  sourceTopicId: z.string().min(1).nullable(),
  mappingScopeId: UUIDSchema,
  primaryGeneralGroupId: UUIDSchema.nullable(),
  taggingNote: AllCharLabelSchema.nullable(),
  reviewSources: z.array(z.string()).nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const TopicSubjectSyllabusSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  groupingId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});

export const TopicRelatedSpecificSchema = z.object({
  topicId: UUIDSchema,
  subjectId: UUIDSchema,
  specificTopicId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});

export const TopicMigrationSchema = z.object({
  id: UUIDSchema,
  subjectId: UUIDSchema,
  legacyTopicId: z.string().min(1),
  kindId: UUIDSchema,
  reason: AllCharLabelSchema.nullable(),
  createdAt: UnixTimestampSchema,
});

export const TopicMigrationTargetSchema = z.object({
  migrationId: UUIDSchema,
  topicId: UUIDSchema,
  position: z.number().int(),
  createdAt: UnixTimestampSchema,
});
